import { useMutation } from "@tanstack/react-query";
import { type ChangeEvent, type ReactElement, useState } from "react";
import { Button } from "../../components/ui/button";
import { ApiError } from "../../lib/api";
import {
  IMPORT_OCTETS_MAX,
  type ImportAncien,
  importAncienSchema,
  type ReponseImport,
  reponseImportSchema,
} from "../contract";
import { SignIn } from "../sign-in";
import { CadreReglages, PANNEAU, TITRE_PANNEAU } from "./cadre";
import {
  IMPORT_ECHEC,
  IMPORT_ECHEC_PAR_STATUT,
  IMPORT_ENVOI,
  IMPORT_FICHIER_INVALIDE,
  IMPORT_FICHIER_LABEL,
  IMPORT_FICHIER_NOTE,
  IMPORT_FICHIER_TROP_LOURD,
  IMPORT_IMPORTER,
  IMPORT_INTRO,
  IMPORT_LECTURE,
  IMPORT_PANNEAU_TITRE,
  IMPORT_RESULTATS_TITRE,
  IMPORT_TITRE,
  importApercu,
  importResultat,
} from "./reglages-texts";

/**
 * La page « Import de l'ancien Castellan » (`/regie/reglages/import`). Elle lit un fichier **local** (la sortie de
 * `scripts/export-ancien.mjs`), le valide par `importAncienSchema` dans le navigateur, montre combien de viewers et
 * de messages il porte, puis l'envoie (`POST /api/direct/import`) sur un clic. Plusieurs fichiers (`-1.json`,
 * `-2.json`) s'envoient l'un apres l'autre ; chaque resultat s'ajoute a la liste.
 *
 * Un fichier hors schema, illisible ou trop lourd donne une phrase et rien ne part. **Aucun pseudo ni texte de
 * message du fichier n'est affiche** : seulement des nombres et le nom du fichier que l'on vient de choisir. Le
 * serveur ne repond que des nombres ; un echec n'affiche que la phrase de son statut.
 */

type Choisi = { nom: string; donnees: ImportAncien };
type Lecture =
  | { etat: "vide" }
  | { etat: "lecture" }
  | { etat: "refuse"; phrase: string }
  | { etat: "pret"; choisi: Choisi };

/** Lit un fichier en texte. `FileReader` plutot que `Blob.text()` : il existe partout, jsdom compris. */
function lireTexte(fichier: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const lecteur = new FileReader();
    lecteur.onload = () => resolve(typeof lecteur.result === "string" ? lecteur.result : "");
    lecteur.onerror = () => reject(new Error("lecture impossible"));
    lecteur.readAsText(fichier);
  });
}

async function envoyer(donnees: ImportAncien): Promise<ReponseImport> {
  const response = await fetch("/api/direct/import", {
    method: "POST",
    headers: { accept: "application/json", "content-type": "application/json" },
    body: JSON.stringify(donnees),
  });
  if (!response.ok) {
    throw new ApiError("import_failed", "Import en echec", response.status);
  }
  const lue = reponseImportSchema.safeParse(await response.json().catch(() => null));
  if (!lue.success) {
    throw new ApiError("contract_broken", "Reponse hors contrat", response.status);
  }
  return lue.data;
}

export function ImportPage(): ReactElement {
  const [lecture, setLecture] = useState<Lecture>({ etat: "vide" });
  // Change a chaque envoi reussi : le champ de fichier repart vide, prêt pour le suivant.
  const [champ, setChamp] = useState(0);
  const [resultats, setResultats] = useState<string[]>([]);

  const envoi = useMutation({
    mutationFn: envoyer,
    gcTime: 0,
  });

  async function choisir(event: ChangeEvent<HTMLInputElement>): Promise<void> {
    const fichier = event.target.files?.[0];
    envoi.reset();
    if (fichier === undefined) {
      setLecture({ etat: "vide" });
      return;
    }
    if (fichier.size > IMPORT_OCTETS_MAX) {
      setLecture({ etat: "refuse", phrase: IMPORT_FICHIER_TROP_LOURD });
      return;
    }
    setLecture({ etat: "lecture" });
    try {
      const lu = importAncienSchema.safeParse(JSON.parse(await lireTexte(fichier)));
      setLecture(
        lu.success
          ? { etat: "pret", choisi: { nom: fichier.name, donnees: lu.data } }
          : { etat: "refuse", phrase: IMPORT_FICHIER_INVALIDE },
      );
    } catch {
      setLecture({ etat: "refuse", phrase: IMPORT_FICHIER_INVALIDE });
    }
  }

  function lancer(): void {
    if (lecture.etat !== "pret") return;
    const { nom, donnees } = lecture.choisi;
    envoi.mutate(donnees, {
      onSuccess: (reponse) => {
        setResultats((avant) => [...avant, importResultat(nom, reponse)]);
        setLecture({ etat: "vide" });
        setChamp((n) => n + 1);
      },
    });
  }

  const statut = envoi.error instanceof ApiError ? envoi.error.status : null;
  if (statut === 401) {
    return (
      <CadreReglages titre={IMPORT_TITRE}>
        <SignIn callbackURL="/regie/reglages/import" />
      </CadreReglages>
    );
  }

  return (
    <CadreReglages titre={IMPORT_TITRE}>
      <section className={PANNEAU} aria-labelledby="import-titre">
        <h2 id="import-titre" className={TITRE_PANNEAU}>
          {IMPORT_PANNEAU_TITRE}
        </h2>
        <p className="m-0 text-muted-foreground">{IMPORT_INTRO}</p>

        <div className="flex flex-col gap-3 rounded-[4px] border-2 border-dashed border-[#7F6AA8] bg-background p-4">
          <label className="flex flex-col gap-1.5">
            {IMPORT_FICHIER_LABEL}
            <input
              key={champ}
              type="file"
              accept="application/json"
              disabled={envoi.isPending}
              onChange={(event) => void choisir(event)}
              className="police-texte text-[20px] text-foreground"
            />
          </label>
          <p className="m-0 text-[19px] text-muted-foreground">{IMPORT_FICHIER_NOTE}</p>

          {lecture.etat === "lecture" ? (
            <p role="status" className="m-0 text-muted-foreground">
              {IMPORT_LECTURE}
            </p>
          ) : null}
          {lecture.etat === "refuse" ? (
            <p role="alert" className="m-0 text-destructive">
              {lecture.phrase}
            </p>
          ) : null}
          {lecture.etat === "pret" ? (
            <p className="m-0" data-testid="apercu">
              {importApercu(
                lecture.choisi.nom,
                lecture.choisi.donnees.viewers.length,
                lecture.choisi.donnees.viewers.reduce(
                  (somme, viewer) => somme + viewer.messagesTexte.length,
                  0,
                ),
              )}
            </p>
          ) : null}

          <div>
            <Button
              className="min-h-12 px-5 text-[22px]"
              disabled={lecture.etat !== "pret" || envoi.isPending}
              onClick={lancer}
            >
              {IMPORT_IMPORTER}
            </Button>
          </div>
          {envoi.isPending ? (
            <p role="status" className="m-0 text-muted-foreground">
              {IMPORT_ENVOI}
            </p>
          ) : null}
          {envoi.isError ? (
            <p role="alert" className="m-0 text-destructive">
              {(statut !== null ? IMPORT_ECHEC_PAR_STATUT[statut] : undefined) ?? IMPORT_ECHEC}
            </p>
          ) : null}
        </div>

        {resultats.length === 0 ? null : (
          <div className="flex flex-col gap-2 rounded-[4px] border-2 border-[#4E8A6E] bg-[#1F2E36] px-4 py-3">
            <h3 className="m-0 text-[22px] font-normal text-[#7FD3A6]">{IMPORT_RESULTATS_TITRE}</h3>
            <ul className="m-0 flex list-none flex-col gap-1 p-0">
              {resultats.map((phrase, index) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: une liste qui ne fait que grandir, sans identifiant
                <li key={index}>{phrase}</li>
              ))}
            </ul>
          </div>
        )}
      </section>
    </CadreReglages>
  );
}
